import { useEffect, useState } from 'react';

import type { Anime, AnimePage } from '@/lib/yozora/types';

/**
 * "Trending on 1Anime" suggestions — the app's own hourly view ranking
 * (VIEWS_HOUR_DESC), the same source as the discover page's section. Fetched
 * once per session and cached in-module so reopening the search palette is
 * instant. Only fetches while `active` (e.g. the palette is open + empty).
 */
const TRENDING_QS = 'page=1&perPage=8&sort=VIEWS_HOUR_DESC';

let cache: Anime[] | null = null;
let inflight: Promise<Anime[]> | null = null;

function fetchTrending(): Promise<Anime[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = fetch(`/api/animes?${TRENDING_QS}`)
    .then(async (res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as AnimePage | { error: string };
      const data = 'data' in body ? (body.data as Anime[]) : [];
      cache = data;
      return data;
    })
    .catch(() => {
      inflight = null;
      return [] as Anime[];
    });
  return inflight;
}

export interface UseTrendingAnime {
  items: Anime[];
  loading: boolean;
}

export function useTrendingAnime(active: boolean): UseTrendingAnime {
  const [items, setItems] = useState<Anime[]>(() => cache ?? []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    if (cache) {
      setItems(cache);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchTrending().then((data) => {
      if (cancelled) return;
      setItems(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return { items, loading };
}

export default useTrendingAnime;
